export const SCRIPT_BASED_PHASE_LABELS = {
  split: 'Split into scenes',
  tts: 'Generate audio',
  align: 'WhisperX alignment',
  prompt: 'Image prompt',
  image: 'Generate image',
};

export function segmentHasWhisperxAlign(seg) {
  if (seg?.alignment_source === 'whisperx') {
    if (seg.align_status === 'completed') return true;
    if (seg.statement_start_time != null && seg.statement_end_time != null) return true;
    if (Array.isArray(seg?.word_timestamps) && seg.word_timestamps.length > 0) return true;
  }
  if (seg?.align_status === 'completed') {
    if (Array.isArray(seg?.word_timestamps) && seg.word_timestamps.length > 0) return true;
    if (seg.statement_start_time != null && seg.statement_end_time != null) return true;
  }
  return false;
}

/** WhisperX or audio-duration fallback — enough to continue the script-based pipeline. */
export function segmentHasAlignTiming(seg) {
  if (segmentHasWhisperxAlign(seg)) return true;
  if (seg?.alignment_source === 'audio_duration' && seg?.align_status === 'completed') {
    return seg.statement_start_time != null || seg.statement_start_time_local != null;
  }
  return false;
}

export function segmentHasImagePrompt(seg) {
  if (!segmentHasAlignTiming(seg)) return false;
  if (Array.isArray(seg?.visual_beats) && seg.visual_beats.length > 0) return true;
  const prompt = (seg?.image_prompt || '').trim();
  if (prompt) return true;
  const visual = (seg?.visual || '').trim();
  const text = (seg?.text || seg?.authored_script || '').trim();
  return visual.length > 0 && visual !== text;
}

export function segmentHasImage(seg) {
  return Boolean((seg?.image_url || seg?.source_image_url || '').trim());
}

/** Ordered pipeline steps with done/current flags for the popup UI.
 *
 * @param {Array} segments
 * @param {string} [visualStyle] - e.g. 'web_scraper'. Web-scraper projects map each
 *   Whisper-timed sentence to a scrape query or an image prompt.
 */
export function buildScriptBasedNextSteps(segments, visualStyle) {
  const isWebScraper = visualStyle === 'web_scraper';
  const audioSegs = (segments || []).filter((s) => s.audio_url);
  const total = audioSegs.length;
  const aligned = audioSegs.filter((s) => segmentHasAlignTiming(s)).length;
  const prompted = audioSegs.filter((s) => segmentHasImagePrompt(s)).length;
  const imaged = audioSegs.filter((s) => segmentHasImage(s)).length;
  const generating = audioSegs.filter((s) => String(s.image_status || '').toLowerCase() === 'running').length;

  const alignDone = total > 0 && aligned >= total;
  const promptsDone = alignDone && prompted >= total;
  const imagesDone = promptsDone && imaged >= total;

  let current = 'align';
  if (imagesDone) current = 'open';
  else if (promptsDone) current = 'images';
  else if (alignDone) current = 'prompts';

  const steps = [
    {
      id: 'align',
      label: 'WhisperX timing',
      detail: total ? `${aligned}/${total} scenes` : 'Waiting for audio',
      done: alignDone,
      current: current === 'align',
    },
  ];

  steps.push({
    id: 'prompts',
    label: isWebScraper ? 'Sentence visual map' : 'Image prompts',
    detail: alignDone ? `${prompted}/${total} scenes` : 'After WhisperX',
    done: promptsDone,
    current: current === 'prompts',
  });

  steps.push(
    {
      id: 'images',
      label: isWebScraper ? 'Scrape or generate images' : 'Generate images',
      detail: promptsDone
        ? `${imaged}/${total} scenes${generating ? ` · ${generating} queued` : ''}`
        : 'After prompts',
      done: imagesDone,
      current: current === 'images',
    },
    {
      id: 'open',
      label: 'Open scenes',
      detail: 'Edit in scene grid',
      done: imagesDone,
      current: current === 'open',
    },
  );

  return steps;
}

export function scriptBasedReadyForScenes(segments, visualStyle) {
  const steps = buildScriptBasedNextSteps(segments, visualStyle);
  return steps.find((s) => s.id === 'open')?.done === true;
}

export function scriptBasedImagesInProgress(segments, job) {
  if (scriptBasedImagesJobStuck(job, segments) || scriptBasedImageStallError(job)) {
    return false;
  }
  const audioSegs = (segments || []).filter((s) => s.audio_url);
  const imaged = audioSegs.filter((s) => segmentHasImage(s)).length;
  const running = audioSegs.filter((s) => String(s.image_status || '').toLowerCase() === 'running').length;
  const pending = audioSegs.filter((s) => {
    if (segmentHasImage(s)) return false;
    const st = String(s.image_status || '').toLowerCase();
    return st === 'pending' || st === 'running' || !st;
  }).length;
  const jobRunning = job && !['completed', 'failed', 'cancelled'].includes(job.status);
  const jobMsg = String(job?.message || '').toLowerCase();
  const jobOnImages = jobMsg.includes('generating image') || jobMsg.includes('resuming image');
  return (jobRunning && jobOnImages) || running > 0 || (pending > 0 && imaged > 0 && imaged < audioSegs.length);
}

export function scriptBasedMissingImageSegments(segments) {
  return (segments || []).filter(
    (s) => s.audio_url && segmentHasAlignTiming(s) && !segmentHasImage(s),
  );
}

export function scriptBasedMissingAlignSegments(segments) {
  return (segments || []).filter((s) => s.audio_url && !segmentHasAlignTiming(s));
}

export function scriptBasedMissingAudioSegments(segments) {
  return (segments || []).filter((s) => {
    const hasText = Boolean((s?.text || s?.authored_script || '').trim());
    const hasAudio = Boolean((s?.audio_url || '').trim());
    if (s?.audio_status === 'failed' || s?.audio_error) return hasText;
    return hasText && !hasAudio;
  });
}

function scriptBasedStuckTimeoutSec(job, step) {
  const key = {
    align: 'script_based_align_stuck_timeout_sec',
    tts: 'script_based_tts_stuck_timeout_sec',
    image: 'script_based_image_stuck_timeout_sec',
    prompt: 'script_based_prompt_stuck_timeout_sec',
  }[step];
  return Number(job?.[key]) || Number(job?.script_based_stuck_timeout_sec) || 300;
}

function jobRunningPhaseIdlePastTimeout(job, timeoutSec, messageMatches) {
  if (!job || job.status !== 'running') return false;
  if (!messageMatches(String(job.message || ''))) return false;
  const updatedAt = job.updated_at ? Date.parse(job.updated_at) : NaN;
  if (!Number.isFinite(updatedAt)) return true;
  return (Date.now() - updatedAt) >= timeoutSec * 1000;
}

function imagePhaseMessage(msg) {
  const lower = String(msg || '').toLowerCase();
  return (
    lower.includes('generating image')
    || lower.includes('resuming image')
    || (lower.includes('image ready') && lower.includes('scene'))
  );
}

export function scriptBasedImageStallError(job) {
  return /image generation stalled/i.test(String(job?.error_message || ''));
}

function alignPhaseMessage(msg) {
  const lower = msg.toLowerCase();
  return lower.includes('whisperx');
}

function ttsPhaseMessage(msg) {
  const lower = msg.toLowerCase();
  return lower.includes('generating audio') || lower.includes('retrying audio') || lower.includes('resuming audio');
}

function scriptBasedResumeInFlight(job, phase) {
  const key = phase === 'image'
    ? 'script_image_resume_in_flight_at'
    : 'script_align_resume_in_flight_at';
  const started = job?.[key];
  if (!started) return false;
  const startedAt = Date.parse(started);
  if (!Number.isFinite(startedAt)) return true;
  const staleSec = Math.max(600, scriptBasedStuckTimeoutSec(job, phase) * 2);
  return (Date.now() - startedAt) < staleSec * 1000;
}

export function scriptBasedImagesJobStuck(job, segments) {
  const missing = scriptBasedMissingImageSegments(segments);
  if (!job) return false;
  if (scriptBasedImageStallError(job)) return true;
  if (!missing.length) return false;
  if (job.status === 'failed') return true;
  if (job.status !== 'running') return false;
  if (!imagePhaseMessage(job.message)) return false;
  return jobRunningPhaseIdlePastTimeout(
    job,
    scriptBasedStuckTimeoutSec(job, 'image'),
    () => true,
  );
}

export function scriptBasedAlignJobStuck(job, segments) {
  const missing = scriptBasedMissingAlignSegments(segments);
  if (!missing.length || !job) return false;
  return jobRunningPhaseIdlePastTimeout(
    job,
    scriptBasedStuckTimeoutSec(job, 'align'),
    alignPhaseMessage,
  );
}

/**
 * WhisperX finished, image prompts are not done, and the running job has gone
 * idle. The Generate prompts button should be clickable in that state.
 */
export function scriptBasedPromptsJobStuck(job, segments) {
  if (!job || job.status !== 'running') return false;
  const audioSegs = (segments || []).filter((s) => s.audio_url);
  if (!audioSegs.length) return false;
  if (audioSegs.some((s) => !segmentHasAlignTiming(s))) return false;
  if (audioSegs.every((s) => segmentHasImagePrompt(s))) return false;
  const lower = String(job.message || '').toLowerCase();
  const generatingPrompts = lower.includes('generating image prompt') || lower.includes('resuming image prompt');
  return jobRunningPhaseIdlePastTimeout(
    job,
    scriptBasedStuckTimeoutSec(job, generatingPrompts ? 'prompt' : 'align'),
    () => true,
  );
}

export function scriptBasedTtsJobStuck(job, segments) {
  const missing = scriptBasedMissingAudioSegments(segments);
  if (!missing.length || !job) return false;
  return jobRunningPhaseIdlePastTimeout(
    job,
    scriptBasedStuckTimeoutSec(job, 'tts'),
    ttsPhaseMessage,
  );
}

/** Show Resume image generation after timeout, failure, or stall error on the job. */
export function scriptBasedShouldShowImageResume(job, segments) {
  if (!job) return false;
  if (job.status === 'cancelled' || job.status === 'completed') return false;
  if (scriptBasedResumeInFlight(job, 'image')) return false;
  const missing = scriptBasedMissingImageSegments(segments);
  if (scriptBasedImageStallError(job)) {
    return missing.length > 0 || job.status === 'failed' || job.status === 'running';
  }
  if (!missing.length) return false;
  if (job.status === 'failed') return true;
  if (job.status === 'running') return scriptBasedImagesJobStuck(job, segments);
  return false;
}

/** Show Resume WhisperX only after timeout or when the job already failed. */
export function scriptBasedShouldShowAlignResume(job, segments) {
  const missing = scriptBasedMissingAlignSegments(segments);
  if (!missing.length || !job) return false;
  if (job.status === 'cancelled') return false;
  if (scriptBasedResumeInFlight(job, 'align')) return false;
  if (job.status === 'failed') return true;
  if (job.status === 'running') return scriptBasedAlignJobStuck(job, segments);
  return false;
}

/** Show resume/retry for audio (initial pipeline TTS) after timeout or failure. */
export function scriptBasedShouldShowAudioResume(job, segments) {
  const missing = scriptBasedMissingAudioSegments(segments);
  if (!missing.length || !job) return false;
  if (job.status === 'cancelled') return false;
  if (missing.some((s) => s.audio_error || s.audio_status === 'failed')) return true;
  if (job.status === 'failed') return true;
  return scriptBasedTtsJobStuck(job, segments);
}

/** @deprecated Use scriptBasedShouldShowImageResume — kept for callers expecting the old name. */
export function scriptBasedCanResumeImages(segments, job) {
  return scriptBasedShouldShowImageResume(job, segments);
}

/** Scenes for reel/list once WhisperX timing is done. */
export function scriptBasedShowSceneViews(segments) {
  const audioSegs = (segments || []).filter((s) => s.audio_url);
  if (!audioSegs.length) return false;
  return audioSegs.every((s) => segmentHasAlignTiming(s));
}
